import {Color} from '@benchling/nucleus-ui';
import {packSx, Stack, useMantineTheme, type StackProps} from '@mantine/core';
import React, {PropsWithChildren} from 'react';

import {BenchlingNodeV2Status} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeV2Constants';
import {useBenchlingNodeV2Context} from 'ui-library/reactflow/nodes/BenchlingNode/v2/useBenchlingNodeV2Context';

const NODE_HEADER_COLOR_NAMES: Record<BenchlingNodeV2Status, string> = {
  error: 'red',
  'in-progress': 'teal',
  pending: 'gray',
  success: 'green',
  warning: 'orange',
};

/**
 * Context-consumer hook to get the background color of a flowchart node header.
 */
const useBenchlingNodeHeaderColor: () => string = () => {
  const {status, isDisabled} = useBenchlingNodeV2Context();
  const theme = useMantineTheme();
  // Special case: if the node is disabled, the header should be gray
  if (isDisabled) {
    return theme.colors.gray[0];
  }
  // Special case: if we don't have a status, default to white
  if (status == null) {
    return theme.white;
  }
  const colorName = NODE_HEADER_COLOR_NAMES[status];
  switch (status) {
    // Special case: pending is one shade darker than the rest of these
    case 'pending':
      return theme.colors[colorName][1];
    case 'error':
    case 'in-progress':
    case 'success':
    case 'warning':
      return theme.colors[colorName][0];
    default:
      return theme.white;
  }
};

/**
 * A lightweight header for a {@link BenchlingNodeV2}. This is a flex column with BenchlingNode styling
 * applied.
 * It consumes the context provided by {@link BenchlingNodeV2}, and as such automatically handles node status
 * and disabled status.
 * @package
 */
export function Header(props: PropsWithChildren<StackProps>): JSX.Element {
  const backgroundColor = useBenchlingNodeHeaderColor();
  const {sx, ...stackProps} = props;
  return (
    <Stack
      bg={backgroundColor}
      spacing="sm"
      p="sm"
      pos="relative"
      sx={[
        (theme) => ({
          borderTopLeftRadius: theme.radius.md,
          borderTopRightRadius: theme.radius.md,
          borderBottom: `1px solid ${Color.GRAY_300}`,
          '&:last-child': {
            borderBottomWidth: 0,
          },
        }),
        ...packSx(sx),
      ]}
      {...stackProps}
    />
  );
}
